/**
 * Loading states for the canvas, shaped like what arrives: palette top left, services in the middle,
 * data on the right, zoom and cost panels at the bottom. Kept free of React Flow so the route can show
 * it while the screen's code is still downloading.
 */
import { Skeleton } from "@/design";

function Card({ h = "h-44" }: { h?: string }) {
  return (
    <div className={`${h} w-60 rounded-xl border border-border bg-surface p-3.5 shadow-xs`}>
      <div className="flex items-center gap-2.5">
        <Skeleton className="size-8 rounded-lg" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-2.5 w-32" />
        </div>
      </div>
      <Skeleton className="mt-4 h-12 w-full rounded-lg" />
      <Skeleton className="mt-3 h-3 w-3/4" />
    </div>
  );
}

const VIEW_KEY = "guv.canvas.view";

/** Canvas or list: what the person last chose, else the list on narrow screens. Shared with the screen. */
export function initialView(): "canvas" | "list" {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    if (v === "canvas" || v === "list") return v;
  } catch {
    /* storage unavailable */
  }
  return typeof window !== "undefined" && window.innerWidth < 640 ? "list" : "canvas";
}

export function rememberView(v: "canvas" | "list") {
  try {
    localStorage.setItem(VIEW_KEY, v);
  } catch {
    /* storage unavailable */
  }
}

/** The list view while it loads: the same groups and rows as the finished list. */
export function CanvasListSkeleton() {
  return (
    <div className="min-h-0 flex-1 overflow-hidden" role="status" aria-label="Loading the canvas">
      <div className="mx-auto w-full max-w-4xl space-y-8 px-4 py-6 sm:px-6">
        {[3, 2].map((rows, g) => (
          <div key={g}>
            <Skeleton className="mb-3 h-4 w-24" />
            <div className="space-y-2">
              {Array.from({ length: rows }, (_, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-border bg-surface px-4 py-3">
                  <Skeleton className="size-9 rounded-lg" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3.5 w-48 max-w-full" />
                  </div>
                  <Skeleton className="hidden h-5 w-28 sm:block" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The body of the canvas while the graph and saved layout load. */
export function CanvasSkeleton({ view = "canvas" }: { view?: "canvas" | "list" }) {
  if (view === "list") return <CanvasListSkeleton />;
  return (
    <div className="guv-canvas-skeleton relative min-h-0 flex-1 overflow-hidden" role="status" aria-label="Loading the canvas">
      <div className="absolute left-3 top-3 hidden w-60 space-y-3 rounded-xl border border-border bg-surface p-4 md:block">
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
      <div className="flex h-full items-center justify-center gap-16 px-4 md:pl-72 lg:gap-24">
        <Card />
        <div className="hidden space-y-10 sm:block">
          <Card />
          <Card h="h-40" />
        </div>
        <div className="hidden space-y-10 lg:block">
          <Card h="h-36" />
          <Card h="h-32" />
        </div>
      </div>
      <Skeleton className="absolute bottom-3 left-3 h-9 w-28 rounded-lg" />
      <Skeleton className="absolute bottom-3 right-3 hidden h-9 w-72 rounded-lg sm:block" />
    </div>
  );
}

/** The whole screen, for the route's pending state (before the screen's code has loaded). */
export function CanvasPending() {
  return (
    <section className="guv-canvas flex h-[calc(100dvh-3.5rem)] flex-col" aria-busy="true">
      <div className="border-b border-border bg-canvas/80 px-3 py-2.5 sm:px-4 lg:px-6">
        <div className="flex items-center gap-2">
          <Skeleton className="h-7 w-28 rounded-full" />
          <Skeleton className="h-7 w-20 rounded-full" />
          <Skeleton className="ml-auto h-7 w-40 rounded-lg" />
        </div>
        <Skeleton className="mt-2 h-4 w-80 max-w-full" />
      </div>
      <CanvasSkeleton view={initialView()} />
    </section>
  );
}
